import { Component } from 'react';

/**
 * DelayedLoad - Drives "delayed + minimum-duration" loading UI timing, with no
 * opinion about what is rendered. It calls children(showLoader), where
 * showLoader becomes true only after `delay` ms of continuous loading, and once
 * true stays true for at least `minDuration` ms. Use it to gate a spinner, a
 * shimmer skeleton, or anything else:
 *
 *   <DelayedLoad isLoading={x}>
 *     {(showLoader) => showLoader ? <Skeleton/> : <Content/>}
 *   </DelayedLoad>
 *
 * The effect: a load that finishes within `delay` never shows the loader (the
 * UI just updates); a slower load shows it, but never for a jarringly brief
 * flash.
 *
 * Props:
 *   isLoading (bool)     - Whether a load is currently in progress
 *   delay (number)       - Ms to wait before showLoader flips true (default: 700)
 *   minDuration (number) - Min ms showLoader stays true once shown (default: 1000)
 *   children (func)      - (showLoader: bool) => ReactNode
 */
class DelayedLoad extends Component {
  constructor(props) {
    super(props);
    this.state = {
      visible: false,
    };
    this._delayTimer = null;
    this._minTimer = null;
    this._shownAt = null;
    this._pendingHide = false;
  }

  componentDidMount() {
    if (this.props.isLoading) {
      this._startDelay();
    }
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.isLoading && this.props.isLoading) {
      // Started loading. Cancel any pending minimum-duration hide left over from
      // a just-finished load, so its tail timer cannot blink the loader off in
      // the middle of this new load.
      if (this._minTimer) {
        clearTimeout(this._minTimer);
        this._minTimer = null;
      }
      this._pendingHide = false;
      this._startDelay();
    } else if (prevProps.isLoading && !this.props.isLoading) {
      // Finished loading
      this._clearDelay();
      if (this.state.visible && this._shownAt) {
        var elapsed = Date.now() - this._shownAt;
        var minDuration = this.props.minDuration !== undefined ? this.props.minDuration : 1000;
        var remaining = minDuration - elapsed;
        if (remaining > 0) {
          this._pendingHide = true;
          var self = this;
          this._minTimer = setTimeout(function() {
            self._minTimer = null;
            self._pendingHide = false;
            self._shownAt = null;
            self.setState({ visible: false });
          }, remaining);
        } else {
          this._shownAt = null;
          this.setState({ visible: false });
        }
      }
    }
  }

  componentWillUnmount() {
    this._clearDelay();
    if (this._minTimer) {
      clearTimeout(this._minTimer);
    }
  }

  _startDelay = function() {
    var self = this;
    var delay = this.props.delay !== undefined ? this.props.delay : 700;
    this._delayTimer = setTimeout(function() {
      self._delayTimer = null;
      if (self.props.isLoading || self._pendingHide) {
        self._shownAt = Date.now();
        self.setState({ visible: true });
      }
    }, delay);
  };

  _clearDelay = function() {
    if (this._delayTimer) {
      clearTimeout(this._delayTimer);
      this._delayTimer = null;
    }
  };

  render() {
    var show = this.state.visible || this._pendingHide;
    var children = this.props.children;
    if (typeof children === 'function') {
      return children(show);
    }
    return null;
  }
}

export default DelayedLoad;
